import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { useStore } from "../context/StoreContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const { t, clearCart } = useStore();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [orderNumber, setOrderNumber] = useState(null);
  const attempts = useRef(0);
  const clearCartRef = useRef(clearCart);
  clearCartRef.current = clearCart;

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    let timer;
    const poll = async () => {
      if (!active || attempts.current >= 12) { if (active) setState("error"); return; }
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (res.data.payment_status === "paid") {
          setOrderNumber(res.data.order_number);
          setState("paid");
          clearCartRef.current();
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) { setState("error"); return; }
      } catch (e) {
        console.warn(`payment status check failed (attempt ${attempts.current})`, e?.response?.status || e?.message);
        if (e?.response?.status === 404) { if (active) setState("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [sessionId]);

  const tp = t.payment;
  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center max-w-md">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-[#C85A32] animate-spin" />
            <h1 className="mt-6 font-serif text-3xl text-stone-900">{tp.verifying}</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}>
              <CheckCircle2 size={56} className="mx-auto text-[#4A624E]" />
            </motion.div>
            <h1 className="mt-6 font-serif text-4xl text-stone-900">{tp.successTitle}</h1>
            <p className="mt-3 text-sm text-stone-600">{tp.successSub}</p>
            {orderNumber && (
              <p className="mt-6 inline-block rounded-full border border-stone-300 px-6 py-2.5 font-mono text-sm text-stone-700" data-testid="payment-order-number">
                {tp.orderNum} {orderNumber}
              </p>
            )}
            <p className="mt-6 text-xs text-stone-500">{tp.accountHint}</p>
            <Link to="/compte" data-testid="payment-account-link" className="mt-3 inline-block text-[12px] uppercase tracking-[0.18em] text-stone-700 border-b border-stone-400 pb-1 hover:text-[#4A624E] hover:border-[#4A624E] transition-colors">
              {t.footer.account}
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={56} className="mx-auto text-[#C85A32]" />
            <h1 className="mt-6 font-serif text-3xl text-stone-900">{tp.errorTitle}</h1>
            <p className="mt-3 text-sm text-stone-600">{tp.errorSub}</p>
          </>
        )}
        <Link to="/" data-testid="payment-back-home" className="mt-10 inline-block rounded-full bg-stone-900 text-[#FAF7F2] px-8 py-3.5 text-[12px] uppercase tracking-[0.2em] hover:bg-[#C85A32] transition-colors duration-300">
          {tp.backHome}
        </Link>
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useStore();
  const tp = t.payment;
  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="payment-cancel-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center max-w-md">
        <XCircle size={56} className="mx-auto text-stone-400" />
        <h1 className="mt-6 font-serif text-4xl text-stone-900">{tp.cancelTitle}</h1>
        <p className="mt-3 text-sm text-stone-600">{tp.cancelSub}</p>
        <Link to="/#products" data-testid="cancel-back-home" className="mt-10 inline-block rounded-full bg-stone-900 text-[#FAF7F2] px-8 py-3.5 text-[12px] uppercase tracking-[0.2em] hover:bg-[#C85A32] transition-colors duration-300">
          {tp.backHome}
        </Link>
      </motion.div>
    </div>
  );
}
